// Tail-spend selectors — the only thing UI imports from the model.

import { DealState, isTerminal, stageLabel } from "./lifecycle";
import {
    Deal,
    DealId,
    GuardrailPolicy,
    Lever,
    Notification,
    ReportKpi,
    Supplier,
    SupplierId,
    Tone,
    Workspace,
} from "./types";

export function getDeal(ws: Workspace, id: DealId): Deal | undefined {
    return ws.deals.find((d) => d.id === id);
}

export function getSupplier(ws: Workspace, id: SupplierId): Supplier | undefined {
    return ws.suppliers.find((s) => s.id === id);
}

export function getPolicyForDeal(ws: Workspace, deal: Deal): GuardrailPolicy {
    const category = ws.categories.find((c) => c.id === deal.categoryId)!;
    return ws.policies.find((p) => p.id === category.policyId)!;
}

export type AutonomyZone = "autonomous" | "esign" | "strategic";

export function getDealAutonomyZone(
    deal: Deal,
    policy: GuardrailPolicy
): AutonomyZone {
    const v = deal.annualValue.amount;
    if (v >= policy.buyerESignBand.min.amount && v <= policy.buyerESignBand.max.amount) {
        return "esign";
    }
    if (v > policy.handToStrategicAbove.amount) return "strategic";
    return "autonomous";
}

export function isLeverOutOfEnvelope(l: Lever): boolean {
    const lo = Math.min(l.targetPct, l.floorPct);
    const hi = Math.max(l.targetPct, l.floorPct);
    return l.currentPct < lo || l.currentPct > hi;
}

export function getLeversScore(deal: Deal): { inEnvelope: number; total: number } {
    const total = deal.envelope.length;
    const inEnvelope = deal.envelope.filter((l) => !isLeverOutOfEnvelope(l)).length;
    return { inEnvelope, total };
}

export type ClauseStatus = "confirmed" | "pending" | "exception";

export function clauseStatusesFor(deal: Deal, clauses: string[]): ClauseStatus[] {
    if (deal.state.kind === "ClosedWon" || deal.state.kind === "Executing") {
        return clauses.map(() => "confirmed");
    }
    if (
        (deal.state.kind === "Escalated" &&
            deal.state.reason === "clause_exception") ||
        deal.escalation?.reason === "clause_exception"
    ) {
        return clauses.map((_, i) =>
            i === 0 ? "confirmed" : i === 1 ? "exception" : "pending"
        );
    }
    if (deal.state.kind === "Held" || deal.state.kind === "Triage") {
        return clauses.map(() => "pending");
    }
    if (deal.state.kind === "Walked" || deal.state.kind === "Reassigned") {
        return clauses.map(() => "pending");
    }
    const confirmedCount = Math.floor(clauses.length / 2);
    return clauses.map((_, i) => (i < confirmedCount ? "confirmed" : "pending"));
}

export function getComplianceScore(
    deal: Deal,
    policy: GuardrailPolicy
): { confirmed: number; total: number } {
    const total = policy.requiredClauses.length;
    const statuses = clauseStatusesFor(deal, policy.requiredClauses);
    const confirmed = statuses.filter((s) => s === "confirmed").length;
    return { confirmed, total };
}

export function getActiveDeals(ws: Workspace): Deal[] {
    return ws.deals.filter(
        (d) =>
            !isTerminal(d.state) &&
            d.state.kind !== "Triage" &&
            d.state.kind !== "Held" &&
            d.state.kind !== "Escalated"
    );
}

export function getNeedsAttentionDeals(ws: Workspace): Deal[] {
    return ws.deals.filter(
        (d) =>
            (d.state.kind === "Escalated" || d.state.kind === "Held") &&
            !d.escalation?.resolution
    );
}

export function getClosedDeals(ws: Workspace): Deal[] {
    return ws.deals.filter((d) => d.state.kind === "ClosedWon");
}

/** KPI strip shown in the Dashboard and Reporting banners. Values that
 * change with the data (in-flight count, needs-decision count) are derived
 * from the workspace; the headline-style metrics stay static. */
export function getBannerKpis(ws: Workspace): ReportKpi[] {
    const inFlight = ws.deals.filter((d) => !isTerminal(d.state)).length;
    const needs = getNeedsAttentionDeals(ws).length;
    return [
        {
            label: "Tail spend under management",
            value: "$487M",
            direction: "flat",
            detail: `${ws.suppliersInScope.toLocaleString()} suppliers in scope`,
        },
        {
            label: "Negotiations in flight",
            value: String(inFlight),
            direction: "up",
            detail: "running across categories",
        },
        {
            label: "Needs your decision",
            value: String(needs),
            direction: "flat",
            detail:
                needs === 0
                    ? "no edge cases pending"
                    : needs === 1
                      ? "1 edge case paused, awaiting you"
                      : `${needs} edge cases paused, awaiting you`,
        },
        {
            label: "Value captured · YTD",
            value: "$2.4M",
            direction: "up",
            detail: "avg cycle 5.0 days · 0 buyer-hours/deal",
        },
    ];
}

/** Parse "In N days" / "N days" out of a renewal window label. */
function parseDaysFromWindow(window: string): number | null {
    const m = window.match(/(\d+)\s*days?/i);
    return m ? parseInt(m[1], 10) : null;
}

/** Deals in Triage state — the agent has detected the renewal trigger but
 * hasn't opened outreach yet. Sorted by closest expiration first using the
 * "When" stat the triage deal carries. */
export function getUpcomingNegotiations(ws: Workspace): Deal[] {
    const upcoming = ws.deals.filter((d) => d.state.kind === "Triage");
    return [...upcoming].sort((a, b) => {
        const aWindow =
            a.statValuePairs.find(([k]) => k === "When")?.[1] ??
            a.resultLabel ??
            "";
        const bWindow =
            b.statValuePairs.find(([k]) => k === "When")?.[1] ??
            b.resultLabel ??
            "";
        const aDays = parseDaysFromWindow(aWindow) ?? 9999;
        const bDays = parseDaysFromWindow(bWindow) ?? 9999;
        return aDays - bDays;
    });
}

export function getAllDealsInOrder(ws: Workspace): Deal[] {
    return [...ws.deals];
}

export function getRecentNotifications(ws: Workspace): Notification[] {
    return ws.notifications;
}

export function getUnreadCount(ws: Workspace): number {
    return ws.notifications.filter((n) => !n.read).length;
}

export function dealStageLabel(deal: Deal): string {
    return deal.stageLabelOverride ?? stageLabel(deal.state);
}

/** Derived status chip from lifecycle state — the sub-state that the stage
 * column doesn't already convey. Empty label means "no detail beyond stage";
 * the table omits the chip in that case. */
export function dealBadge(state: DealState, deal: Deal): { label: string; tone: Tone } {
    switch (state.kind) {
        case "Triage":
            return { label: "", tone: "slate" };
        case "Negotiating": {
            const who =
                state.awaiting === "supplier"
                    ? "awaiting supplier"
                    : "agent’s turn";
            const head =
                state.round === 1 && state.awaiting === "supplier"
                    ? `Outreach · ${who}`
                    : `Round ${state.round} · ${who}`;
            return {
                label: state.manual ? `${head} · manual` : head,
                tone: "teal",
            };
        }
        case "AwaitingConfirmation":
            return { label: `Round ${state.round}`, tone: "teal" };
        case "Escalated":
            switch (state.reason) {
                case "floor_breach":
                    return { label: "Floor breach", tone: "red" };
                case "supplier_silent":
                    return { label: "Supplier silent", tone: "amber" };
                case "ladder_exhausted":
                    return { label: "Ladder exhausted", tone: "red" };
                case "esign_band":
                    return { label: "E-sign band", tone: "blue" };
                case "clause_exception":
                    return { label: "Clause exception", tone: "red" };
                case "low_confidence_benchmark":
                    return { label: "Low-confidence benchmark", tone: "amber" };
                case "above_autonomous_cap":
                    return { label: "Above cap", tone: "amber" };
            }
            return { label: "", tone: "red" };
        case "Held":
            return { label: "Above cap", tone: "amber" };
        case "Executing":
            return {
                label: state.routing === "buyer_esign" ? "Buyer e-sign" : "Auto-execute",
                tone: "blue",
            };
        case "ClosedWon":
            return { label: deal.outcome ? "Executed" : "", tone: "green" };
        case "Walked":
            return { label: "", tone: "slate" };
        case "Reassigned":
            return { label: "Strategic team", tone: "slate" };
    }
}

export function dealResultLabel(state: DealState, deal: Deal): string {
    if (state.kind === "ClosedWon" && deal.outcome) {
        return deal.outcome.savingsLabel;
    }
    if (state.kind === "Escalated" || state.kind === "Held") {
        return deal.escalation?.resolution ? "Decided · resuming" : "Awaiting you";
    }
    if (state.kind === "Reassigned") return "Reassigned";
    if (state.kind === "Walked") return "Walked";
    return "In progress";
}
